import type { Direction, Point, SnakeGame } from "./types.js";

const OPPOSITE: Record<Direction, Direction> = {
  UP: "DOWN",
  DOWN: "UP",
  LEFT: "RIGHT",
  RIGHT: "LEFT",
};

function randomFood(snake: Point[], cols: number, rows: number): Point {
  const occupied = new Set(snake.map((p) => `${p.x},${p.y}`));
  const free: Point[] = [];
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      if (!occupied.has(`${x},${y}`)) free.push({ x, y });
    }
  }
  if (free.length === 0) return { x: -1, y: -1 };
  return free[Math.floor(Math.random() * free.length)];
}

export function createGame(cols: number = 20, rows: number = 20): SnakeGame {
  const midX = Math.floor(cols / 2);
  const midY = Math.floor(rows / 2);
  const snake: Point[] = [
    { x: midX, y: midY },
    { x: midX - 1, y: midY },
    { x: midX - 2, y: midY },
  ];
  return {
    snake,
    food: randomFood(snake, cols, rows),
    direction: "RIGHT",
    nextDirection: "RIGHT",
    score: 0,
    highScore: 0,
    isOver: false,
    isPaused: true,
    gridCols: cols,
    gridRows: rows,
    speed: 200,
  };
}

export function changeDirection(game: SnakeGame, dir: Direction): SnakeGame {
  if (dir === OPPOSITE[game.direction]) return game;
  return { ...game, nextDirection: dir };
}

export function tick(game: SnakeGame): SnakeGame {
  if (game.isOver || game.isPaused) return game;

  const direction = game.nextDirection;
  const head = game.snake[0];
  const newHead: Point = { x: head.x, y: head.y };

  switch (direction) {
    case "UP": newHead.y -= 1; break;
    case "DOWN": newHead.y += 1; break;
    case "LEFT": newHead.x -= 1; break;
    case "RIGHT": newHead.x += 1; break;
  }

  if (
    newHead.x < 0 || newHead.x >= game.gridCols ||
    newHead.y < 0 || newHead.y >= game.gridRows
  ) {
    return { ...game, isOver: true, highScore: Math.max(game.score, game.highScore) };
  }

  if (game.snake.some((p) => p.x === newHead.x && p.y === newHead.y)) {
    return { ...game, isOver: true, highScore: Math.max(game.score, game.highScore) };
  }

  const ate = newHead.x === game.food.x && newHead.y === game.food.y;
  const newSnake = [newHead, ...game.snake];
  if (!ate) newSnake.pop();

  const newSpeed = ate ? Math.max(80, game.speed - 1) : game.speed;

  return {
    ...game,
    snake: newSnake,
    direction,
    food: ate ? randomFood(newSnake, game.gridCols, game.gridRows) : game.food,
    score: ate ? game.score + 1 : game.score,
    highScore: Math.max(ate ? game.score + 1 : game.score, game.highScore),
    speed: newSpeed,
    isOver: ate && newSnake.length >= game.gridCols * game.gridRows,
  };
}
